/**
 * PackView — Asset Pack Homepage
 *
 * Two-panel layout mirroring BriefWorkspace:
 *  - Left (scrollable): Share card, grounding summary, buyer scenarios, ad gallery
 *  - Right (collapsible sidecar, 380 → 48px): Pack Actions — quick nav chips,
 *    share token details, links to Canvas / Brief / ZIP download
 *
 * Accessible at /creative/packs/:packId?job=<jobId>
 * The ?job= param is injected by AssetsLens so we can mint a share token.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Code2,
  Copy,
  Download,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Info,
  Layers,
  Link2,
  Loader2,
  MessageSquare,
  Palette,
  Plus,
  RefreshCw,
  Share2,
  Sparkles,
  Target,
  User,
  X,
  Zap,
} from 'lucide-react';
import clsx from 'clsx';
import {
  ChatPanel,
  useChat,
  useMentionables,
  type ChatMention,
  type MentionItem,
  type MentionRef,
} from '@neurograph/skill-apps/creative-studio';
import { AssetDetailModal, type ModalAsset } from '@/components/AssetDetailModal';
import { adPackShareApi, getApiBase, type ShareTokenResponse } from '@/services/api';

// ─── Constants ───────────────────────────────────────────────────────────────

const AGENT_BASE_URL =
  getApiBase() || (typeof window !== 'undefined' ? window.location.origin : '');

const SIDECAR_WIDTH = 280;
const SIDECAR_COLLAPSED_WIDTH = 48;
const CHAT_WIDTH = 360;
const CHAT_COLLAPSED_WIDTH = 48;

function canvasUrl(packId: string) {
  return `${AGENT_BASE_URL.replace(/\/$/, '')}/canvas/${encodeURIComponent(packId)}`;
}

function zipUrl(packId: string) {
  return `${AGENT_BASE_URL.replace(/\/$/, '')}/api/v1/creative-canvas/asset_pack/${encodeURIComponent(packId)}/export`;
}

// ─── Wire types ──────────────────────────────────────────────────────────────

interface CreativeConcept {
  id: string;
  label: string;
  strength: number;
}

interface GroundingManifest {
  brief_id: string;
  persona_id: string;
  client_uid: string;
  scenario_ids: string[];
  creative_concepts: CreativeConcept[];
  brand_summary: string;
}

interface SeedSignals {
  visual_style?: string;
  hook?: string;
  angle?: string;
  emotion?: string;
}

interface SeedAnalysis {
  composition?: {
    focal_point?: string;
    hero_placement?: string;
    text_zones?: string[];
    layout?: string;
  };
  palette?: {
    dominant?: string[];
    accent?: string[];
    temperature?: string;
  };
  photography?: {
    style?: string;
    lighting?: string;
    depth_of_field?: string;
    mood?: string;
  };
  structure?: {
    text_density?: string;
    logo_placement?: string;
    layout_archetype?: string;
  };
  conceptual?: {
    hook_archetype?: string;
    emotional_register?: string;
    primary_claim?: string;
  };
}

interface PackSeed {
  creative_id: string;
  mode: string;
  framing: string;
  thumbnail_url?: string;
  source_url?: string;
  name?: string;
  inherited_signals?: SeedSignals;
  analysis?: SeedAnalysis;
}

interface AssetPack {
  asset_pack_id: string;
  client_uid: string;
  brief_id: string | null;
  persona_id: string | null;
  status: string;
  grounding: GroundingManifest;
  summary: Record<string, unknown>;
  created_at: string;
  seed?: PackSeed | null;
}

// ─── API helpers ─────────────────────────────────────────────────────────────

async function apiFetchPack(packId: string): Promise<AssetPack> {
  const r = await fetch(
    `${AGENT_BASE_URL}/api/v1/creative-canvas/asset_pack/${encodeURIComponent(packId)}`,
  );
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json() as Promise<AssetPack>;
}

async function apiFetchFinalAds(packId: string): Promise<ModalAsset[]> {
  const r = await fetch(
    `${AGENT_BASE_URL}/api/v1/creative-canvas/asset_pack/${encodeURIComponent(packId)}/tracks/final_ad`,
  );
  if (!r.ok) return [];
  const data = (await r.json()) as { assets?: ModalAsset[] };
  return data.assets ?? [];
}

// ─── Template detail types (mirrors FormatTemplateResponse / VisualStyleTokenResponse) ──

interface FormatTemplateDetail {
  template_id: string;
  template_name: string;
  description: string;
  quality_tier: string;
  funnel_stage: string;
  composition_clarity: number;
  best_for_personas: string[];
  best_for_scenarios: string[];
  recommended_for_channels: string[];
  mood_descriptors?: string[];
  zone_count: number;
  zone_names: string[];
  hero_ad_image_url: string | null;
  associated_ad_image_urls: string[];
  exemplar_count: number;
}

interface VisualStyleTokenDetail {
  token_id: string;
  token_name: string;
  quality_tier: string;
  funnel_stage: string;
  mood_descriptors: string[];
  photography_descriptors: string[];
  lighting_preference: string;
  color_temperature: string;
  hero_ad_image_url: string | null;
  associated_ad_image_urls: string[];
  best_for_personas: string[];
  best_for_scenarios: string[];
}

async function apiFetchFormatTemplate(templateId: string): Promise<FormatTemplateDetail | null> {
  try {
    const r = await fetch(
      `${AGENT_BASE_URL}/api/v1/ad-templates/format-templates/${encodeURIComponent(templateId)}`,
    );
    if (!r.ok) return null;
    return r.json() as Promise<FormatTemplateDetail>;
  } catch {
    return null;
  }
}

async function apiFetchVisualStyleToken(tokenId: string): Promise<VisualStyleTokenDetail | null> {
  try {
    const r = await fetch(
      `${AGENT_BASE_URL}/api/v1/ad-templates/visual-style-tokens/${encodeURIComponent(tokenId)}`,
    );
    if (!r.ok) return null;
    return r.json() as Promise<VisualStyleTokenDetail>;
  } catch {
    return null;
  }
}

// ─── PackView ────────────────────────────────────────────────────────────────

export function PackView() {
  const { packId } = useParams<{ packId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const jobId = searchParams.get('job') ?? null;

  const [pack, setPack] = useState<AssetPack | null>(null);
  const [assets, setAssets] = useState<ModalAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<ModalAsset | null>(null);
  const [sidecarCollapsed, setSidecarCollapsed] = useState(false);
  const [shareCardDismissed, setShareCardDismissed] = useState(false);
  // Auto-collapse the sidecar a few seconds after the pack loads so the
  // gallery gets more horizontal space. The countdown toast lets the user
  // cancel before it fires.
  const [sidecarCountdown, setSidecarCountdown] = useState<number | null>(null);
  const sidecarAutoFiredRef = useRef(false);

  // Chat sidebar state — defaults to visible once the pack is completed,
  // collapsed otherwise so the user isn't staring at an idle chat while
  // the pack is still generating.
  const [chatCollapsed, setChatCollapsed] = useState(true);
  const [imageOverrides, setImageOverrides] = useState<Map<string, string>>(new Map());

  // Share token state
  const [shareToken, setShareToken] = useState<ShareTokenResponse | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [urlCopied, setUrlCopied] = useState(false);

  useEffect(() => {
    if (!packId) return;
    setLoading(true);
    setError(null);
    Promise.all([apiFetchPack(packId), apiFetchFinalAds(packId)])
      .then(([p, a]) => { setPack(p); setAssets(a); })
      .catch((e: unknown) => setError((e as Error).message ?? 'Failed to load'))
      .finally(() => setLoading(false));
  }, [packId]);

  // Resolve client_uid from the loaded pack — required to scope the admin
  // chat endpoints. Until both packId and clientUid are present, useChat is
  // mounted but inert (no API calls fire).
  const clientUid = pack?.grounding?.client_uid ?? pack?.client_uid ?? '';
  const chatAuth = useMemo(
    () => ({
      mode: 'admin' as const,
      packId: packId ?? '',
      clientUid,
      apiBase: AGENT_BASE_URL,
    }),
    [packId, clientUid],
  );
  const chat = useChat(chatAuth);
  const mentionItems: MentionItem[] = useMentionables(chatAuth);

  // Default chat visibility: open once the pack completes, otherwise collapsed.
  // Run only when the *status string* changes — once the user toggles, we
  // honor their choice for the remainder of the session.
  const userToggledChatRef = useRef(false);
  useEffect(() => {
    if (userToggledChatRef.current) return;
    if (pack?.status === 'completed') {
      setChatCollapsed(false);
    }
  }, [pack?.status]);

  // Auto-collapse the Generation Context sidecar 4 seconds after the pack
  // loads. A 3-tick countdown toast warns the user and gives them a chance
  // to cancel by clicking "Keep open".
  useEffect(() => {
    if (!pack || sidecarAutoFiredRef.current || sidecarCollapsed) return;
    sidecarAutoFiredRef.current = true;
    setSidecarCountdown(3);
  }, [pack, sidecarCollapsed]);

  useEffect(() => {
    if (sidecarCountdown === null) return;
    if (sidecarCountdown === 0) {
      setSidecarCollapsed(true);
      setSidecarCountdown(null);
      return;
    }
    const t = setTimeout(() => setSidecarCountdown((c) => (c !== null ? c - 1 : null)), 1000);
    return () => clearTimeout(t);
  }, [sidecarCountdown]);

  const handleChatToggle = useCallback(() => {
    userToggledChatRef.current = true;
    setChatCollapsed((c) => !c);
  }, []);

  // When an edit completes, swap in the new image URL across the gallery.
  const handleImageUpdate = useCallback((adBriefId: string, newUrl: string) => {
    setImageOverrides((prev) => {
      const next = new Map(prev);
      next.set(adBriefId, newUrl);
      return next;
    });
  }, []);

  const allAdBriefIds = useMemo(
    () => assets.map((a) => a.grounding.brief_id).filter(Boolean),
    [assets],
  );

  const handleChatSelectAll = useCallback(() => {
    chat.selectAllAds(allAdBriefIds);
  }, [chat, allAdBriefIds]);

  const handleChatSend = useCallback(
    (content: string, mentions: MentionRef[] = []) => {
      const chatMentions: ChatMention[] = mentions.map((m) => ({
        type: m.type,
        id: m.id,
        label: m.label,
      }));
      chat.sendMessage(content, handleImageUpdate, undefined, chatMentions);
    },
    [chat, handleImageUpdate],
  );

  // Apply image overrides on top of the asset list so live edits show up
  // immediately in the gallery without re-fetching from the canvas API.
  const liveAssets = useMemo(() => {
    if (imageOverrides.size === 0) return assets;
    return assets.map((a) => {
      const override = imageOverrides.get(a.grounding.brief_id);
      if (!override) return a;
      return { ...a, media_url: override, thumbnail_url: override };
    });
  }, [assets, imageOverrides]);

  // Auto-mint share token once the pack is loaded so client_uid is available.
  // Without the `loading` guard this fires before pack data arrives, sending
  // no client_uid and triggering a 404 from the backend.
  // `shareLoading` is intentionally excluded from deps — it's managed by this
  // effect and including it causes an infinite retry loop on errors.
  useEffect(() => {
    if (!jobId || loading || shareToken || shareLoading || shareError) return;
    setShareLoading(true);
    adPackShareApi
      .mintForJob(jobId, pack?.client_uid)
      .then((token) => setShareToken(token))
      .catch((e) => setShareError(e instanceof Error ? e.message : 'Failed to create share link'))
      .finally(() => setShareLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, loading, pack?.client_uid, shareToken, shareError]);

  const handleMintShare = useCallback(async () => {
    if (!jobId) return;
    setShareLoading(true);
    setShareError(null);
    try {
      const token = await adPackShareApi.mintForJob(jobId, pack?.client_uid);
      setShareToken(token);
    } catch (e) {
      setShareError(e instanceof Error ? e.message : 'Failed to create share link');
    } finally {
      setShareLoading(false);
    }
  }, [jobId, pack?.client_uid]);

  const handleCopyShareUrl = useCallback(() => {
    if (!shareToken) return;
    void navigator.clipboard.writeText(shareToken.share_url).then(() => {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    });
  }, [shareToken]);

  // Group assets by primary creative concept (uses liveAssets so override
  // edits appear in the gallery without a full refetch)
  const conceptGroups = useMemo(() => {
    const groups = new Map<string, ModalAsset[]>();
    for (const asset of liveAssets) {
      const key = asset.grounding.creative_concepts[0]?.label ?? 'Generated Ads';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(asset);
    }
    return groups;
  }, [liveAssets]);

  // Unique scenarios across assets
  const scenarioIds = useMemo(() => {
    const seen = new Set<string>();
    for (const a of assets) {
      if (a.grounding.scenario_id) seen.add(a.grounding.scenario_id);
    }
    return Array.from(seen);
  }, [assets]);

  // Aggregate exemplar template / style-token IDs across the pack.
  // Per-ad attribution lives on `generation_params.extras`, populated by
  // the ad-pack pipeline's TemplateExemplarResolver wiring.
  const templatePool = useMemo(() => {
    const templateIds = new Set<string>();
    const styleTokenIds = new Set<string>();
    for (const a of assets) {
      const extras = a.generation_params?.extras;
      if (!extras) continue;
      const tIds = extras['exemplar_template_ids'];
      if (Array.isArray(tIds)) {
        for (const id of tIds) if (typeof id === 'string') templateIds.add(id);
      }
      const sIds = extras['exemplar_visual_style_token_ids'];
      if (Array.isArray(sIds)) {
        for (const id of sIds) if (typeof id === 'string') styleTokenIds.add(id);
      }
    }
    return {
      templateIds: Array.from(templateIds),
      styleTokenIds: Array.from(styleTokenIds),
    };
  }, [assets]);

  const meanCes = useMemo(() => {
    const scores = assets.map((a) => a.scores?.['ces']).filter((s): s is number => s != null);
    if (!scores.length) return null;
    return Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100);
  }, [assets]);

  const displayName = useMemo(() => {
    const personaId = pack?.grounding?.persona_id ?? pack?.persona_id;
    if (personaId) {
      return personaId.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    }
    return packId ? `${packId.slice(0, 20)}…` : 'Asset Pack';
  }, [pack, packId]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 size={24} className="text-slate-300 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 text-center px-6">
        <p className="text-sm text-rose-600">Failed to load pack: {error}</p>
        <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-700 underline">
          Go back
        </button>
      </div>
    );
  }

  const briefPath = pack?.grounding?.brief_id || pack?.brief_id
    ? `/creative/briefs/${pack.grounding?.brief_id ?? pack.brief_id}`
    : null;

  return (
    <div className="flex flex-col h-[calc(100vh-2rem)] -mx-4 -my-4 bg-slate-50">
      {/* ── Sticky header ── */}
      <PackHeader
        displayName={displayName}
        assets={liveAssets}
        meanCes={meanCes}
        clientUid={pack?.grounding?.client_uid ?? pack?.client_uid ?? null}
        packId={packId ?? null}
        chatCollapsed={chatCollapsed}
        onChatToggle={handleChatToggle}
        onBack={() => navigate(-1)}
        onDownload={packId ? () => window.open(zipUrl(packId), '_blank') : undefined}
        shareToken={shareToken}
        shareLoading={shareLoading}
        urlCopied={urlCopied}
        onMintShare={handleMintShare}
        onCopyShareUrl={handleCopyShareUrl}
      />

      {/* ── Body: left sidecar | center scroll | right chat ── */}
      <div className="flex flex-1 min-h-0">
        {/* Left: Generation Context sidecar (collapsible) */}
        <PackSidecar
          packId={packId ?? null}
          briefPath={briefPath}
          collapsed={sidecarCollapsed}
          templateIds={templatePool.templateIds}
          styleTokenIds={templatePool.styleTokenIds}
          grounding={pack?.grounding ?? null}
          scenarioIds={scenarioIds}
          seed={pack?.seed}
          countdown={sidecarCountdown}
          onExpand={() => { setSidecarCollapsed(false); setSidecarCountdown(null); sidecarAutoFiredRef.current = true; }}
          onCollapse={() => { setSidecarCollapsed(true); setSidecarCountdown(null); }}
          onKeepOpen={() => { setSidecarCountdown(null); sidecarAutoFiredRef.current = true; }}
        />

        {/* Center: scrollable content */}
        <div className="flex-1 min-w-0 overflow-y-auto">
          <div className={clsx(
            'mx-auto px-6 py-6 space-y-6 transition-[max-width] duration-300',
            sidecarCollapsed ? 'max-w-6xl' : 'max-w-4xl',
          )}>
            {/* Share card — dismissible */}
            {!shareCardDismissed && (
              <ShareCard
                jobId={jobId}
                shareToken={shareToken}
                shareLoading={shareLoading}
                shareError={shareError}
                urlCopied={urlCopied}
                onCopy={handleCopyShareUrl}
                onMint={handleMintShare}
                onDismiss={() => setShareCardDismissed(true)}
              />
            )}

            {/* Grounding + scenarios */}
            <PackGrounding
              grounding={pack?.grounding ?? null}
              scenarioIds={scenarioIds}
            />

            {/* Asset gallery */}
            {liveAssets.length === 0 ? (
              <EmptyGallery />
            ) : (
              Array.from(conceptGroups.entries()).map(([concept, conceptAssets]) => (
                <ConceptSection
                  key={concept}
                  concept={concept}
                  assets={conceptAssets}
                  activeAdIds={chat.activeAdIds}
                  sidecarCollapsed={sidecarCollapsed}
                  onSelect={setSelectedAsset}
                  onToggleChat={chat.toggleAd}
                />
              ))
            )}
          </div>
        </div>

        {/* Right: collapsible chat sidebar */}
        <ChatSidecar
          collapsed={chatCollapsed}
          onExpand={() => {
            userToggledChatRef.current = true;
            setChatCollapsed(false);
          }}
          onCollapse={() => {
            userToggledChatRef.current = true;
            setChatCollapsed(true);
          }}
          messages={chat.messages}
          activeAdIds={chat.activeAdIds}
          isStreaming={chat.isStreaming}
          totalAdCount={allAdBriefIds.length}
          mentionItems={mentionItems}
          chatError={chat.error}
          onSend={handleChatSend}
          onCancel={chat.cancelStream}
          onSelectAll={handleChatSelectAll}
          onClearSelection={chat.clearSelection}
          onRetry={chat.retrySession}
        />
      </div>

      {/* Asset detail modal */}
      {selectedAsset && packId && (
        <AssetDetailModal
          asset={selectedAsset}
          packId={packId}
          onClose={() => setSelectedAsset(null)}
          onNavigateToTemplate={(templateId) => {
            setSelectedAsset(null);
            navigate(`/creative-templates/${templateId}`);
          }}
        />
      )}
    </div>
  );
}

// ─── Pack Header ─────────────────────────────────────────────────────────────

function PackHeader({
  displayName,
  assets,
  meanCes,
  clientUid,
  packId,
  chatCollapsed,
  onChatToggle,
  onBack,
  onDownload,
  shareToken,
  shareLoading,
  urlCopied,
  onMintShare,
  onCopyShareUrl,
}: {
  displayName: string;
  assets: ModalAsset[];
  meanCes: number | null;
  clientUid: string | null;
  packId: string | null;
  chatCollapsed: boolean;
  onChatToggle: () => void;
  onBack: () => void;
  onDownload?: () => void;
  shareToken: ShareTokenResponse | null;
  shareLoading: boolean;
  urlCopied: boolean;
  onMintShare: () => void;
  onCopyShareUrl: () => void;
}) {
  const [embedCopied, setEmbedCopied] = useState(false);

  const handleCopyEmbed = () => {
    if (!packId) return;
    const snippet = `<iframe src="${canvasUrl(packId)}" width="1200" height="800" style="border:none;border-radius:12px;" allowfullscreen></iframe>`;
    void navigator.clipboard.writeText(snippet).then(() => {
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 2000);
    });
  };

  return (
    <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={onBack}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700 transition shrink-0"
            >
              <ArrowLeft size={14} />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-sm font-bold text-slate-900 truncate">{displayName}</h1>
                <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">
                  completed
                </span>
              </div>
              <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                <span className="text-[10px] text-slate-400 flex items-center gap-1">
                  <Sparkles size={9} /> {assets.length} ads
                </span>
                {meanCes !== null && (
                  <span className="text-[10px] text-slate-400 flex items-center gap-1">
                    <Layers size={9} /> CES {meanCes}
                  </span>
                )}
                {clientUid && <span className="text-[10px] text-slate-400">{clientUid}</span>}
                {packId && (
                  <span className="font-mono text-[9px] text-slate-300 hidden md:block">{packId}</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Share button */}
            {shareLoading ? (
              <div className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-400 bg-white border border-slate-200 rounded-lg">
                <Loader2 size={12} className="animate-spin" />
                <span className="hidden sm:inline">Creating link…</span>
              </div>
            ) : shareToken ? (
              <button
                onClick={onCopyShareUrl}
                title="Copy share link"
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition',
                  urlCopied
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300',
                )}
              >
                {urlCopied ? <Check size={13} /> : <Share2 size={13} />}
                <span className="hidden sm:inline">{urlCopied ? 'Copied!' : 'Share'}</span>
              </button>
            ) : (
              <button
                onClick={onMintShare}
                title="Create share link"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-brand-teal bg-white border border-brand-teal/40 rounded-lg hover:bg-teal-50 transition"
              >
                <Share2 size={13} />
                <span className="hidden sm:inline">Share</span>
              </button>
            )}
            {onDownload && (
              <button
                onClick={onDownload}
                title="Download asset ZIP"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition"
              >
                <Download size={13} />
                <span className="hidden sm:inline">Download Assets</span>
              </button>
            )}
            <button
              onClick={onChatToggle}
              title={chatCollapsed ? 'Open chat' : 'Close chat'}
              className={clsx(
                'w-8 h-8 flex items-center justify-center rounded-lg border transition',
                chatCollapsed
                  ? 'border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600'
                  : 'border-teal-300 bg-teal-50 text-teal-600',
              )}
            >
              <MessageSquare size={13} />
            </button>
            <button
              onClick={handleCopyEmbed}
              title="Copy embed snippet"
              className={clsx(
                'w-8 h-8 flex items-center justify-center rounded-lg border transition',
                embedCopied
                  ? 'border-teal-300 bg-teal-50 text-teal-600'
                  : 'border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600',
              )}
            >
              {embedCopied ? <Check size={13} /> : <Code2 size={13} />}
            </button>
            {packId && (
              <a
                href={canvasUrl(packId)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-xl hover:from-violet-600 hover:to-fuchsia-600 transition shadow-sm"
              >
                <Sparkles size={13} /> Open in Canvas
                <ExternalLink size={10} className="opacity-70" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Share Card ──────────────────────────────────────────────────────────────

function ShareCard({
  jobId,
  shareToken,
  shareLoading,
  shareError,
  urlCopied,
  onCopy,
  onMint,
  onDismiss,
}: {
  jobId: string | null;
  shareToken: ShareTokenResponse | null;
  shareLoading: boolean;
  shareError: string | null;
  urlCopied: boolean;
  onCopy: () => void;
  onMint: () => void;
  onDismiss?: () => void;
}) {
  if (!jobId && !shareToken) return null;

  return (
    <div className="bg-gradient-to-r from-brand-teal/10 via-white to-brand-purple/10 border border-brand-teal/30 rounded-2xl p-5 shadow-soft relative">
      {onDismiss && (
        <button
          onClick={onDismiss}
          title="Dismiss"
          className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
        >
          <X size={13} />
        </button>
      )}
      <div className="flex flex-col gap-4 pr-8">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-brand-teal text-white flex items-center justify-center flex-shrink-0">
            <Share2 size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-bold text-navy-900 leading-snug">Share this ad pack</h2>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Send this link to a client or teammate to review, comment on, and take the pack with
              them. No login required.
            </p>
          </div>
        </div>

        {shareToken ? (
          <div className="flex items-center gap-1.5 sm:gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2.5 w-full min-w-0 shadow-sm">
            <Share2 size={14} className="text-slate-500 flex-shrink-0" aria-hidden />
            <span className="flex-1 font-mono text-[11px] sm:text-xs text-slate-700 truncate min-w-0">
              {shareToken.share_url}
            </span>
            <div className="flex items-center gap-0.5 shrink-0 border-l border-slate-200 pl-2 ml-1">
              <button
                type="button"
                onClick={onCopy}
                className="flex items-center justify-center rounded-lg p-2 text-slate-700 hover:text-brand-teal hover:bg-brand-teal/10 active:bg-brand-teal/15 transition-colors"
                title="Copy URL"
              >
                {urlCopied ? (
                  <Check size={17} className="text-emerald-600" strokeWidth={2.25} />
                ) : (
                  <Copy size={17} strokeWidth={2.25} />
                )}
              </button>
              <a
                href={shareToken.share_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center rounded-lg p-2 text-slate-700 hover:text-brand-teal hover:bg-brand-teal/10 active:bg-brand-teal/15 transition-colors"
                title="Open in new tab"
              >
                <ExternalLink size={17} strokeWidth={2.25} />
              </a>
            </div>
          </div>
        ) : shareLoading ? (
          <div className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium text-slate-500 bg-white border border-slate-200 rounded-lg">
            <Loader2 size={12} className="animate-spin" />
            Creating share link…
          </div>
        ) : (
          <button
            onClick={onMint}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-teal rounded-lg hover:bg-brand-teal-dark transition-colors w-fit"
          >
            <Share2 size={14} />
            Create share link
          </button>
        )}
      </div>

      {shareError && (
        <div className="mt-3 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-600">
          {shareError}
        </div>
      )}

      {shareToken && (
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-200/70 text-[10px] text-slate-500">
          <span>
            <span className="font-bold text-slate-400 uppercase tracking-wide mr-1">Expires</span>
            {new Date(shareToken.expires_at).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
          <span>
            <span className="font-bold text-slate-400 uppercase tracking-wide mr-1">Edit credits</span>
            {shareToken.edits_remaining} remaining
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Pack Grounding ───────────────────────────────────────────────────────────

function PackGrounding({
  grounding,
  scenarioIds,
}: {
  grounding: GroundingManifest | null;
  scenarioIds: string[];
}) {
  if (!grounding) return null;

  const topConcepts = grounding.creative_concepts?.slice(0, 8) ?? [];

  return (
    <div className="space-y-4">
      {/* Primary grounding row */}
      <div className="grid grid-cols-1 gap-4">
        {/* Scenarios covered */}
        {scenarioIds.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center">
                <Target size={11} className="text-teal-500" />
              </div>
              <span className="text-[10px] font-bold uppercase text-teal-600 tracking-wider">
                Buyer Scenarios
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {scenarioIds.slice(0, 4).map((s) => (
                <span
                  key={s}
                  className="text-[9px] bg-teal-50 text-teal-700 border border-teal-200 px-1.5 py-0.5 rounded-full capitalize"
                >
                  {s.replace(/_/g, ' ')}
                </span>
              ))}
              {scenarioIds.length > 4 && (
                <span className="text-[9px] text-slate-400">+{scenarioIds.length - 4}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Creative concepts strip */}
      {topConcepts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center">
              <Zap size={11} className="text-amber-500" />
            </div>
            <span className="text-[10px] font-bold uppercase text-amber-600 tracking-wider">
              Creative Concepts
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {topConcepts.map((c) => (
              <span
                key={c.id}
                className="flex items-center gap-1 text-[10px] bg-amber-50 text-amber-800 border border-amber-200 px-2 py-1 rounded-full"
              >
                <span
                  className="w-1 h-1 rounded-full bg-amber-400"
                  style={{ opacity: Math.max(0.4, c.strength ?? 1) }}
                />
                {c.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Brand voice */}
      {grounding.brand_summary && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
              <Layers size={11} className="text-slate-500" />
            </div>
            <span className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">Brand Voice</span>
          </div>
          <p className="text-[11px] text-slate-600 leading-relaxed">{grounding.brand_summary}</p>
        </div>
      )}
    </div>
  );
}


// ─── Concept Section ──────────────────────────────────────────────────────────

function ConceptSection({
  concept,
  assets,
  activeAdIds,
  sidecarCollapsed,
  onSelect,
  onToggleChat,
}: {
  concept: string;
  assets: ModalAsset[];
  activeAdIds: string[];
  sidecarCollapsed: boolean;
  onSelect: (a: ModalAsset) => void;
  onToggleChat: (adBriefId: string) => void;
}) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <Target size={12} className="text-teal-500 shrink-0" />
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-600">{concept}</h2>
        <span className="text-xs text-slate-400">({assets.length})</span>
      </div>
      <div className={clsx(
        'grid gap-3',
        sidecarCollapsed
          ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
          : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
      )}>
        {assets.map((asset) => (
          <GalleryTile
            key={asset.asset_id}
            asset={asset}
            isInChat={
              !!asset.grounding.brief_id && activeAdIds.includes(asset.grounding.brief_id)
            }
            onClick={() => onSelect(asset)}
            onToggleChat={() => {
              if (asset.grounding.brief_id) onToggleChat(asset.grounding.brief_id);
            }}
          />
        ))}
      </div>
    </section>
  );
}

function EmptyGallery() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center bg-white border border-slate-200 rounded-2xl">
      <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
        <ImageIcon size={24} className="text-slate-300" />
      </div>
      <p className="text-sm text-slate-500 font-medium">No final ads in this pack yet</p>
      <p className="text-xs text-slate-400 mt-1">Assets may still be processing — check back shortly.</p>
    </div>
  );
}

// ─── Gallery Tile ─────────────────────────────────────────────────────────────

function GalleryTile({
  asset,
  isInChat,
  onClick,
  onToggleChat,
}: {
  asset: ModalAsset;
  isInChat: boolean;
  onClick: () => void;
  onToggleChat: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const url = asset.thumbnail_url || asset.media_url;
  const ces = asset.scores?.['ces'];
  const scenario = asset.grounding.scenario_id?.replace(/_/g, ' ') ?? null;

  const handleToggleChat = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleChat();
    },
    [onToggleChat],
  );

  return (
    <div
      className={clsx(
        'group cursor-pointer rounded-2xl overflow-hidden border bg-white hover:shadow-md transition-all',
        isInChat
          ? 'border-brand-teal ring-2 ring-brand-teal/30'
          : 'border-slate-200 hover:border-violet-300',
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      <div className="relative aspect-square bg-slate-100">
        {url ? (
          <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon size={24} className="text-slate-300" />
          </div>
        )}
        {/* Chat-toggle affordance: top-left "+" / "✓" pill */}
        <button
          type="button"
          onClick={handleToggleChat}
          title={isInChat ? 'Remove from chat' : 'Add to chat'}
          className={clsx(
            'absolute top-2 left-2 w-6 h-6 flex items-center justify-center rounded-full text-[10px] font-bold backdrop-blur-sm border transition',
            isInChat
              ? 'bg-brand-teal text-white border-brand-teal'
              : 'bg-white/70 text-slate-600 border-white/60 hover:bg-white hover:border-slate-300',
          )}
        >
          {isInChat ? <X size={12} /> : <Plus size={12} />}
        </button>
        {ces != null && !hovered && (
          <div className="absolute top-2 right-2 text-[9px] font-bold bg-black/50 text-white px-1.5 py-0.5 rounded-full backdrop-blur-sm">
            {Math.round(ces * 100)}
          </div>
        )}
        <div
          className={clsx(
            'absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity duration-150 pointer-events-none',
            hovered ? 'opacity-100' : 'opacity-0',
          )}
        >
          <span className="text-xs font-semibold text-white bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">
            View Details
          </span>
        </div>
      </div>
      <div className="px-3 py-2.5">
        {scenario && (
          <p className="text-[10px] font-semibold text-teal-600 truncate mb-0.5">{scenario}</p>
        )}
        {ces != null && (
          <p className="text-[10px] text-slate-400">
            CES <span className="font-bold text-emerald-600">{Math.round(ces * 100)}</span>
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Pack Sidecar ─────────────────────────────────────────────────────────────

interface PackSidecarProps {
  packId: string | null;
  briefPath: string | null;
  collapsed: boolean;
  templateIds: string[];
  styleTokenIds: string[];
  grounding: GroundingManifest | null;
  scenarioIds: string[];
  seed?: PackSeed | null;
  countdown: number | null;
  onExpand: () => void;
  onCollapse: () => void;
  onKeepOpen: () => void;
}

function PackSidecar({
  briefPath,
  collapsed,
  templateIds,
  styleTokenIds,
  grounding,
  scenarioIds,
  seed,
  countdown,
  onExpand,
  onCollapse,
  onKeepOpen,
}: PackSidecarProps) {
  const navigate = useNavigate();

  // Fetch template/token details when the panel is expanded
  const [templateDetails, setTemplateDetails] = useState<Map<string, FormatTemplateDetail>>(
    new Map(),
  );
  const [tokenDetails, setTokenDetails] = useState<Map<string, VisualStyleTokenDetail>>(
    new Map(),
  );
  const [templatesFetched, setTemplatesFetched] = useState(false);

  // Reference detail modal state
  const [referenceDetail, setReferenceDetail] = useState<ReferenceDetailTarget | null>(null);

  useEffect(() => {
    if (collapsed || templatesFetched) return;
    setTemplatesFetched(true);

    for (const id of templateIds) {
      apiFetchFormatTemplate(id).then((data) => {
        if (!data) return;
        setTemplateDetails((prev) => new Map(prev).set(id, data));
      });
    }
    for (const id of styleTokenIds) {
      apiFetchVisualStyleToken(id).then((data) => {
        if (!data) return;
        setTokenDetails((prev) => new Map(prev).set(id, data));
      });
    }
  }, [collapsed, templatesFetched, templateIds, styleTokenIds]);

  return (
    <aside
      className="flex flex-col border-r border-slate-200 bg-white h-full overflow-hidden transition-[width] duration-200 ease-out flex-shrink-0"
      style={{ width: collapsed ? SIDECAR_COLLAPSED_WIDTH : SIDECAR_WIDTH }}
    >
      {collapsed ? (
        /* ── Collapsed strip ── */
        <div className="flex flex-col items-center pt-3 gap-1 pb-4">
          <button
            onClick={onExpand}
            title="View generation context"
            className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-500 hover:bg-indigo-100 transition mb-1"
          >
            <ChevronRight size={15} />
          </button>
          <div className="w-5 border-t border-slate-100 my-1" />
          <button
            onClick={onExpand}
            title="Generation context"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-indigo-400 hover:bg-indigo-50 transition"
          >
            <Info size={14} />
          </button>
          {briefPath && (
            <button
              onClick={() => { onExpand(); navigate(briefPath); }}
              title="View brief"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-amber-500 hover:bg-amber-50 transition"
            >
              <FileText size={14} />
            </button>
          )}
        </div>
      ) : (
        /* ── Expanded panel ── */
        <div className="flex flex-col h-full">
          {/* Header */}
          <header className="px-4 py-3 border-b border-slate-200 flex-shrink-0">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h2 className="text-sm font-bold text-slate-900">Generation Context</h2>
                <p className="text-[11px] text-slate-500 mt-0.5">Settings &amp; templates that drove these ads</p>
              </div>
              <button
                onClick={onCollapse}
                title="Collapse panel"
                className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
              >
                <ChevronLeft size={14} />
              </button>
            </div>
            {/* Auto-collapse countdown toast */}
            {countdown !== null && (
              <div className="mt-2 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                <span className="text-[10px] text-slate-500 flex-1">
                  Collapsing in <span className="font-bold text-slate-700">{countdown}s</span> to free space
                </span>
                <button
                  onClick={onKeepOpen}
                  className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 whitespace-nowrap"
                >
                  Keep open
                </button>
              </div>
            )}
          </header>

          <div className="flex-1 overflow-y-auto p-4 space-y-5">

            {/* Strategy — persona, brief, scenarios */}
            {(grounding?.persona_id || briefPath || scenarioIds.length > 0) && (
              <section>
                <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-2">
                  Strategy
                </p>
                <div className="space-y-2">
                  {grounding?.persona_id && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-violet-50 border border-violet-100 rounded-lg">
                      <User size={11} className="text-violet-400 shrink-0" />
                      <span className="text-xs font-semibold text-violet-700 capitalize">
                        {grounding.persona_id.replace(/_/g, ' ')}
                      </span>
                      <span className="ml-auto text-[9px] font-bold uppercase text-violet-400 tracking-wide">Persona</span>
                    </div>
                  )}
                  {briefPath && (
                    <button
                      onClick={() => navigate(briefPath)}
                      className="flex items-center gap-2 w-full px-3 py-2 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-100 rounded-lg hover:bg-amber-100 transition text-left"
                    >
                      <FileText size={11} />
                      View source brief
                      <ChevronRight size={9} className="ml-auto opacity-60" />
                    </button>
                  )}
                  {scenarioIds.length > 0 && (
                    <div className="px-3 py-2 bg-teal-50 border border-teal-100 rounded-lg">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Target size={10} className="text-teal-500" />
                        <span className="text-[10px] font-bold uppercase text-teal-600 tracking-wide">
                          Buyer Scenarios
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {scenarioIds.slice(0, 5).map((s) => (
                          <span
                            key={s}
                            className="text-[9px] bg-white text-teal-700 border border-teal-200 px-1.5 py-0.5 rounded-full capitalize"
                          >
                            {s.replace(/_/g, ' ')}
                          </span>
                        ))}
                        {scenarioIds.length > 5 && (
                          <span className="text-[9px] text-teal-500">+{scenarioIds.length - 5}</span>
                        )}
                      </div>
                    </div>
                  )}
                  {grounding?.brand_summary && (
                    <div className="px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg">
                      <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wide mb-1">Brand Voice</p>
                      <p className="text-[10px] text-slate-500 leading-relaxed line-clamp-3">{grounding.brand_summary}</p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* ── Reference Sources (Seed Ad + Ad Templates unified) ── */}
            <section>
              {/* Section header with total reference count */}
              <div className="flex items-center gap-1.5 mb-2">
                <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                  Reference Sources
                </p>
                {(seed || templateIds.length > 0) && (
                  <span className="text-[10px] text-slate-300">
                    ({(seed ? 1 : 0) + templateIds.length})
                  </span>
                )}
              </div>

              {/* Combined empty state when neither seed nor templates were used */}
              {!seed && templateIds.length === 0 ? (
                <div className="px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50">
                  <p className="text-[11px] font-semibold text-slate-600 mb-0.5">
                    No references used
                  </p>
                  <p className="text-[10px] text-slate-500 leading-relaxed">
                    This pack was generated freeform — no seed ad or template composition
                    was used to anchor it. Seed a winning ad or add templates to guide
                    the next run.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">

                  {/* ── Seed Ad card (amber — performance-anchored) ── */}
                  {seed && (
                    <div
                      className="border border-amber-100 rounded-xl overflow-hidden bg-white cursor-pointer hover:border-amber-300 hover:shadow-sm transition"
                      onClick={() => setReferenceDetail({ kind: 'seed', data: seed })}
                      title="Click to see full extraction details"
                    >
                      {/* Type label row */}
                      <div className="flex items-center gap-1.5 px-2.5 pt-2 pb-1">
                        <span className="text-[8px] font-bold uppercase tracking-wider text-amber-500 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded-full">
                          Seed Ad
                        </span>
                        <span className="text-[9px] text-slate-400 truncate flex-1">
                          {seed.name ?? seed.creative_id}
                        </span>
                        {seed.source_url && (
                          <a
                            href={seed.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-300 hover:text-slate-500 flex-shrink-0"
                            title="View source ad"
                          >
                            <ExternalLink size={10} />
                          </a>
                        )}
                      </div>

                      {/* Thumbnail */}
                      {seed.thumbnail_url ? (
                        <div className="relative mx-2.5 rounded-lg overflow-hidden mb-2">
                          <img
                            src={seed.thumbnail_url}
                            alt={seed.name ?? seed.creative_id}
                            className="w-full aspect-video object-cover bg-slate-100"
                            loading="lazy"
                          />
                          <div className="absolute top-1.5 left-1.5 flex gap-1">
                            <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-black/50 text-white backdrop-blur-sm">
                              {seed.mode === 'same_strategy' ? 'Same Strategy'
                                : seed.mode === 'explore_new_angles' ? 'New Angles'
                                : seed.mode}
                            </span>
                            <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-black/50 text-white backdrop-blur-sm">
                              {seed.framing === 'composition_match' ? 'Composition'
                                : seed.framing === 'style_match' ? 'Style'
                                : seed.framing === 'strict_template' ? 'Strict'
                                : seed.framing === 'none' ? 'No Image'
                                : seed.framing}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="mx-2.5 mb-2 rounded-lg w-full aspect-video bg-amber-50 flex items-center justify-center">
                          <div className="text-center">
                            <ImageIcon size={16} className="text-amber-200 mx-auto mb-1" />
                            <span className="text-[9px] text-amber-300">No preview</span>
                          </div>
                        </div>
                      )}

                      <div className="px-2.5 pb-2.5 space-y-2">
                        {/* Mode + Framing pills */}
                        <div className="flex flex-wrap gap-1">
                          <span className="text-[9px] bg-amber-50 text-amber-700 border border-amber-100 px-1.5 py-0.5 rounded-full">
                            {seed.mode === 'same_strategy' ? 'Same Strategy'
                              : seed.mode === 'explore_new_angles' ? 'Explore New Angles'
                              : seed.mode}
                          </span>
                          <span className="text-[9px] bg-slate-50 text-slate-600 border border-slate-100 px-1.5 py-0.5 rounded-full">
                            {seed.framing === 'composition_match' ? 'Composition match'
                              : seed.framing === 'style_match' ? 'Style only'
                              : seed.framing === 'strict_template' ? 'Strict template'
                              : seed.framing === 'none' ? 'Metadata only'
                              : seed.framing}
                          </span>
                        </div>

                        {/* Inherited signals */}
                        {seed.inherited_signals && Object.values(seed.inherited_signals).some(Boolean) && (
                          <div>
                            <p className="text-[9px] font-bold uppercase text-slate-400 tracking-wide mb-1">
                              Inherited signals
                            </p>
                            <div className="space-y-0.5">
                              {seed.inherited_signals.visual_style && (
                                <div className="flex gap-2 text-[10px]">
                                  <span className="text-slate-400 w-14 flex-shrink-0">visual</span>
                                  <span className="text-slate-700">{seed.inherited_signals.visual_style}</span>
                                </div>
                              )}
                              {seed.inherited_signals.hook && (
                                <div className="flex gap-2 text-[10px]">
                                  <span className="text-slate-400 w-14 flex-shrink-0">hook</span>
                                  <span className="text-slate-700">{seed.inherited_signals.hook}</span>
                                </div>
                              )}
                              {seed.inherited_signals.angle && (
                                <div className="flex gap-2 text-[10px]">
                                  <span className="text-slate-400 w-14 flex-shrink-0">angle</span>
                                  <span className="text-slate-700">{seed.inherited_signals.angle}</span>
                                </div>
                              )}
                              {seed.inherited_signals.emotion && (
                                <div className="flex gap-2 text-[10px]">
                                  <span className="text-slate-400 w-14 flex-shrink-0">emotion</span>
                                  <span className="text-slate-700">{seed.inherited_signals.emotion}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Pre-flight analysis */}
                        {seed.analysis && (
                          <div>
                            <p className="text-[9px] font-bold uppercase text-slate-400 tracking-wide mb-1">
                              Visual analysis
                            </p>
                            <div className="space-y-0.5">
                              {seed.analysis.composition?.focal_point && (
                                <div className="flex gap-2 text-[10px]">
                                  <span className="text-slate-400 w-16 flex-shrink-0">composition</span>
                                  <span className="text-slate-600 line-clamp-1">{seed.analysis.composition.focal_point}</span>
                                </div>
                              )}
                              {seed.analysis.palette?.temperature && (
                                <div className="flex gap-2 text-[10px]">
                                  <span className="text-slate-400 w-16 flex-shrink-0">palette</span>
                                  <span className="text-slate-600 capitalize">
                                    {seed.analysis.palette.temperature}
                                    {seed.analysis.palette.dominant?.length
                                      ? ` · ${seed.analysis.palette.dominant.slice(0, 2).join(', ')}`
                                      : ''}
                                  </span>
                                </div>
                              )}
                              {seed.analysis.photography?.style && (
                                <div className="flex gap-2 text-[10px]">
                                  <span className="text-slate-400 w-16 flex-shrink-0">photo style</span>
                                  <span className="text-slate-600">{seed.analysis.photography.style.replace(/_/g, ' ')}</span>
                                </div>
                              )}
                              {seed.analysis.structure?.text_density && (
                                <div className="flex gap-2 text-[10px]">
                                  <span className="text-slate-400 w-16 flex-shrink-0">text density</span>
                                  <span className="text-slate-600">{seed.analysis.structure.text_density}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="text-[9px] font-mono text-slate-300 truncate">
                          {seed.creative_id}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Template cards (indigo — structure-anchored) ── */}
                  {templateIds.map((id) => {
                    const t = templateDetails.get(id);
                    return (
                      <div
                        key={id}
                        className="border border-indigo-100 rounded-xl overflow-hidden bg-white cursor-pointer hover:border-indigo-300 hover:shadow-sm transition"
                        onClick={() => setReferenceDetail({ kind: 'template', id, data: t })}
                        title="Click to see full template details"
                      >
                        {/* Type label row */}
                        <div className="flex items-center gap-1.5 px-2.5 pt-2 pb-1">
                          <span className="text-[8px] font-bold uppercase tracking-wider text-indigo-500 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded-full">
                            Template
                          </span>
                          <span className="text-[9px] text-slate-400 truncate flex-1">
                            {t?.template_name ?? id}
                          </span>
                          {t?.quality_tier && (
                            <span className={clsx(
                              'text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full flex-shrink-0',
                              t.quality_tier === 'exemplar'
                                ? 'bg-amber-100 text-amber-600'
                                : 'bg-slate-100 text-slate-500',
                            )}>
                              {t.quality_tier}
                            </span>
                          )}
                        </div>

                        {/* Reference image */}
                        {t?.hero_ad_image_url ? (
                          <div className="mx-2.5 mb-2 rounded-lg overflow-hidden">
                            <img
                              src={t.hero_ad_image_url}
                              alt={t.template_name}
                              className="w-full aspect-video object-cover bg-slate-100"
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <div className="mx-2.5 mb-2 rounded-lg w-full aspect-video bg-indigo-50 flex items-center justify-center">
                            {t ? (
                              <span className="text-[9px] text-indigo-300">No reference image</span>
                            ) : (
                              <Loader2 size={14} className="text-indigo-300 animate-spin" />
                            )}
                          </div>
                        )}

                        <div className="px-2.5 pb-2.5">
                          {t?.description && (
                            <p className="text-[10px] text-slate-500 leading-relaxed line-clamp-2 mb-2">
                              {t.description}
                            </p>
                          )}
                          {t && (
                            <div className="flex flex-wrap gap-1">
                              {t.funnel_stage && (
                                <span className="text-[9px] bg-indigo-50 text-indigo-600 border border-indigo-100 px-1.5 py-0.5 rounded-full capitalize">
                                  {t.funnel_stage}
                                </span>
                              )}
                              {t.zone_names.slice(0, 3).map((z) => (
                                <span key={z} className="text-[9px] bg-slate-50 text-slate-500 border border-slate-100 px-1.5 py-0.5 rounded-full">
                                  {z}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                </div>
              )}
            </section>

            {/* Visual Style Tokens */}
            {styleTokenIds.length > 0 && (
              <section>
                <div className="flex items-center gap-1.5 mb-2">
                  <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                    Visual Style
                  </p>
                  <span className="text-[10px] text-slate-300">({styleTokenIds.length})</span>
                </div>
                <div className="space-y-3">
                  {styleTokenIds.map((id) => {
                    const t = tokenDetails.get(id);
                    return (
                      <div
                        key={id}
                        className="border border-fuchsia-100 rounded-xl overflow-hidden bg-white cursor-pointer hover:border-fuchsia-300 hover:shadow-sm transition"
                        onClick={() => setReferenceDetail({ kind: 'token', id, data: t })}
                        title="Click to see full style token details"
                      >
                        {t?.hero_ad_image_url ? (
                          <img
                            src={t.hero_ad_image_url}
                            alt={t.token_name}
                            className="w-full aspect-video object-cover bg-slate-100"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full aspect-video bg-fuchsia-50 flex items-center justify-center">
                            {t ? (
                              <Palette size={16} className="text-fuchsia-300" />
                            ) : (
                              <Loader2 size={14} className="text-fuchsia-300 animate-spin" />
                            )}
                          </div>
                        )}
                        <div className="p-2.5">
                          <p className="text-xs font-semibold text-slate-800">
                            {t?.token_name ?? id}
                          </p>
                          {t?.mood_descriptors && t.mood_descriptors.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {t.mood_descriptors.slice(0, 4).map((m) => (
                                <span
                                  key={m}
                                  className="text-[9px] bg-fuchsia-50 text-fuchsia-600 border border-fuchsia-100 px-1.5 py-0.5 rounded-full"
                                >
                                  {m}
                                </span>
                              ))}
                            </div>
                          )}
                          {t?.lighting_preference && (
                            <p className="text-[10px] text-slate-400 mt-1 capitalize">
                              {t.lighting_preference} · {t.color_temperature}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

          </div>
        </div>
      )}

      {/* Reference detail modal */}
      {referenceDetail && (
        <ReferenceDetailModal
          target={referenceDetail}
          onClose={() => setReferenceDetail(null)}
        />
      )}
    </aside>
  );
}

// ─── Reference Detail Modal ───────────────────────────────────────────────────

type ReferenceDetailTarget =
  | { kind: 'seed'; data: PackSeed }
  | { kind: 'template'; id: string; data: FormatTemplateDetail | undefined }
  | { kind: 'token'; id: string; data: VisualStyleTokenDetail | undefined };

function ReferenceDetailModal({
  target,
  onClose,
}: {
  target: ReferenceDetailTarget;
  onClose: () => void;
}) {
  const isSeed = target.kind === 'seed';
  const isTemplate = target.kind === 'template';
  const isToken = target.kind === 'token';

  const imageUrl = isSeed
    ? target.data.thumbnail_url
    : isTemplate
      ? target.data?.hero_ad_image_url
      : target.data?.hero_ad_image_url;

  const title = isSeed
    ? (target.data.name ?? target.data.creative_id)
    : isTemplate
      ? (target.data?.template_name ?? target.id)
      : (target.data?.token_name ?? target.id);

  const accentColor = isSeed ? 'amber' : isTemplate ? 'indigo' : 'fuchsia';
  const badgeLabel = isSeed ? 'Seed Ad' : isTemplate ? 'Template' : 'Visual Style';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className={`px-6 py-4 border-b border-${accentColor}-100 bg-${accentColor}-50/40 flex items-start justify-between gap-4 flex-shrink-0`}>
          <div className="flex items-center gap-2.5 min-w-0">
            <span className={`text-[9px] font-bold uppercase tracking-wider text-${accentColor}-600 bg-${accentColor}-100 border border-${accentColor}-200 px-2 py-1 rounded-full flex-shrink-0`}>
              {badgeLabel}
            </span>
            <h2 className="text-sm font-bold text-slate-900 truncate">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition flex-shrink-0"
          >
            <X size={14} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Large reference image */}
          {imageUrl ? (
            <div className="relative bg-slate-100">
              <img
                src={imageUrl}
                alt={title}
                className="w-full max-h-72 object-contain"
              />
            </div>
          ) : (
            <div className={`w-full h-48 bg-${accentColor}-50 flex items-center justify-center`}>
              <ImageIcon size={32} className={`text-${accentColor}-200`} />
            </div>
          )}

          <div className="p-6 space-y-5">
            {/* ── SEED AD DETAILS ── */}
            {isSeed && (
              <>
                {/* Mode + Framing */}
                <div>
                  <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-2">Generation settings</p>
                  <div className="flex flex-wrap gap-2">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[9px] text-slate-400 uppercase tracking-wide">Mode</span>
                      <span className={`text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100 px-2.5 py-1 rounded-full`}>
                        {target.data.mode === 'same_strategy' ? 'Same Strategy'
                          : target.data.mode === 'explore_new_angles' ? 'Explore New Angles'
                          : target.data.mode}
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[9px] text-slate-400 uppercase tracking-wide">Visual framing</span>
                      <span className="text-xs font-semibold bg-slate-50 text-slate-700 border border-slate-200 px-2.5 py-1 rounded-full">
                        {target.data.framing === 'composition_match' ? 'Composition match'
                          : target.data.framing === 'style_match' ? 'Style only'
                          : target.data.framing === 'strict_template' ? 'Strict template'
                          : target.data.framing === 'none' ? 'Metadata only'
                          : target.data.framing}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Inherited signals */}
                {target.data.inherited_signals && Object.values(target.data.inherited_signals).some(Boolean) && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-2">Inherited signals</p>
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-3.5 space-y-2">
                      {target.data.inherited_signals.visual_style && (
                        <div className="flex gap-3 text-sm">
                          <span className="text-slate-400 w-20 flex-shrink-0 text-xs pt-px">Visual style</span>
                          <span className="text-slate-800 leading-snug">{target.data.inherited_signals.visual_style}</span>
                        </div>
                      )}
                      {target.data.inherited_signals.hook && (
                        <div className="flex gap-3 text-sm">
                          <span className="text-slate-400 w-20 flex-shrink-0 text-xs pt-px">Hook</span>
                          <span className="text-slate-800 leading-snug">{target.data.inherited_signals.hook}</span>
                        </div>
                      )}
                      {target.data.inherited_signals.angle && (
                        <div className="flex gap-3 text-sm">
                          <span className="text-slate-400 w-20 flex-shrink-0 text-xs pt-px">Angle</span>
                          <span className="text-slate-800 leading-snug">{target.data.inherited_signals.angle}</span>
                        </div>
                      )}
                      {target.data.inherited_signals.emotion && (
                        <div className="flex gap-3 text-sm">
                          <span className="text-slate-400 w-20 flex-shrink-0 text-xs pt-px">Emotion</span>
                          <span className="text-slate-800 leading-snug">{target.data.inherited_signals.emotion}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Visual analysis */}
                {target.data.analysis && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-2">Visual analysis extracted</p>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2">
                      {target.data.analysis.composition && (
                        <div>
                          <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Composition</p>
                          {target.data.analysis.composition.focal_point && (
                            <div className="flex gap-3 text-sm mb-1">
                              <span className="text-slate-400 w-24 flex-shrink-0 text-xs pt-px">Focal point</span>
                              <span className="text-slate-700">{target.data.analysis.composition.focal_point}</span>
                            </div>
                          )}
                          {target.data.analysis.composition.layout && (
                            <div className="flex gap-3 text-sm">
                              <span className="text-slate-400 w-24 flex-shrink-0 text-xs pt-px">Layout</span>
                              <span className="text-slate-700 capitalize">{target.data.analysis.composition.layout}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {target.data.analysis.palette && (
                        <div>
                          <p className="text-[10px] font-bold uppercase text-slate-400 mb-1 mt-2">Palette</p>
                          {target.data.analysis.palette.temperature && (
                            <div className="flex gap-3 text-sm mb-1">
                              <span className="text-slate-400 w-24 flex-shrink-0 text-xs pt-px">Temperature</span>
                              <span className="text-slate-700 capitalize">{target.data.analysis.palette.temperature}</span>
                            </div>
                          )}
                          {target.data.analysis.palette.dominant && target.data.analysis.palette.dominant.length > 0 && (
                            <div className="flex gap-3 text-sm">
                              <span className="text-slate-400 w-24 flex-shrink-0 text-xs pt-px">Dominant</span>
                              <div className="flex flex-wrap gap-1">
                                {target.data.analysis.palette.dominant.map((c: string) => (
                                  <span key={c} className="text-[10px] bg-white border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded">{c}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {target.data.analysis.photography && (
                        <div>
                          <p className="text-[10px] font-bold uppercase text-slate-400 mb-1 mt-2">Photography</p>
                          {target.data.analysis.photography.style && (
                            <div className="flex gap-3 text-sm mb-1">
                              <span className="text-slate-400 w-24 flex-shrink-0 text-xs pt-px">Style</span>
                              <span className="text-slate-700">{target.data.analysis.photography.style.replace(/_/g, ' ')}</span>
                            </div>
                          )}
                          {target.data.analysis.photography.lighting && (
                            <div className="flex gap-3 text-sm">
                              <span className="text-slate-400 w-24 flex-shrink-0 text-xs pt-px">Lighting</span>
                              <span className="text-slate-700 capitalize">{target.data.analysis.photography.lighting}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {target.data.analysis.structure && (
                        <div>
                          <p className="text-[10px] font-bold uppercase text-slate-400 mb-1 mt-2">Structure</p>
                          {target.data.analysis.structure.text_density && (
                            <div className="flex gap-3 text-sm mb-1">
                              <span className="text-slate-400 w-24 flex-shrink-0 text-xs pt-px">Text density</span>
                              <span className="text-slate-700 capitalize">{target.data.analysis.structure.text_density}</span>
                            </div>
                          )}
                          {target.data.analysis.structure.logo_placement && (
                            <div className="flex gap-3 text-sm">
                              <span className="text-slate-400 w-24 flex-shrink-0 text-xs pt-px">Logo placement</span>
                              <span className="text-slate-700 capitalize">{target.data.analysis.structure.logo_placement}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ID */}
                <div className="text-[10px] font-mono text-slate-300">{target.data.creative_id}</div>
              </>
            )}

            {/* ── TEMPLATE DETAILS ── */}
            {isTemplate && (
              <>
                {target.data?.description && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-2">Description</p>
                    <p className="text-sm text-slate-700 leading-relaxed">{target.data.description}</p>
                  </div>
                )}

                {target.data && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-2">Properties</p>
                    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3.5 space-y-2">
                      {target.data.quality_tier && (
                        <div className="flex gap-3 text-sm">
                          <span className="text-slate-400 w-28 flex-shrink-0 text-xs pt-px">Quality tier</span>
                          <span className="text-slate-800 capitalize font-semibold">{target.data.quality_tier}</span>
                        </div>
                      )}
                      {target.data.funnel_stage && (
                        <div className="flex gap-3 text-sm">
                          <span className="text-slate-400 w-28 flex-shrink-0 text-xs pt-px">Funnel stage</span>
                          <span className="text-slate-800 capitalize">{target.data.funnel_stage}</span>
                        </div>
                      )}
                      {target.data.composition_clarity > 0 && (
                        <div className="flex gap-3 text-sm">
                          <span className="text-slate-400 w-28 flex-shrink-0 text-xs pt-px">Composition score</span>
                          <span className="text-slate-800">{(target.data.composition_clarity * 100).toFixed(0)}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {target.data?.zone_names && target.data.zone_names.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-2">
                      Composition zones ({target.data.zone_names.length})
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {target.data.zone_names.map((z) => (
                        <span key={z} className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 px-2.5 py-1 rounded-full">
                          {z}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {target.data?.mood_descriptors && target.data.mood_descriptors.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-2">Mood descriptors</p>
                    <div className="flex flex-wrap gap-1.5">
                      {target.data.mood_descriptors.map((m) => (
                        <span key={m} className="text-xs bg-indigo-50 text-indigo-600 border border-indigo-100 px-2.5 py-1 rounded-full">
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="text-[10px] font-mono text-slate-300">{target.id}</div>
              </>
            )}

            {/* ── VISUAL STYLE TOKEN DETAILS ── */}
            {isToken && (
              <>
                {target.data?.mood_descriptors && target.data.mood_descriptors.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-2">Mood descriptors</p>
                    <div className="flex flex-wrap gap-1.5">
                      {target.data.mood_descriptors.map((m) => (
                        <span key={m} className="text-xs bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-100 px-2.5 py-1 rounded-full">
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {target.data && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-2">Style properties</p>
                    <div className="bg-fuchsia-50 border border-fuchsia-100 rounded-xl p-3.5 space-y-2">
                      {target.data.lighting_preference && (
                        <div className="flex gap-3 text-sm">
                          <span className="text-slate-400 w-28 flex-shrink-0 text-xs pt-px">Lighting</span>
                          <span className="text-slate-800 capitalize">{target.data.lighting_preference}</span>
                        </div>
                      )}
                      {target.data.color_temperature && (
                        <div className="flex gap-3 text-sm">
                          <span className="text-slate-400 w-28 flex-shrink-0 text-xs pt-px">Color temp</span>
                          <span className="text-slate-800 capitalize">{target.data.color_temperature}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="text-[10px] font-mono text-slate-300">{target.id}</div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ChatSidecarProps {
  collapsed: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  messages: import('@neurograph/skill-apps/creative-studio').ChatMessage[];
  activeAdIds: string[];
  isStreaming: boolean;
  totalAdCount: number;
  mentionItems: MentionItem[];
  chatError: string | null;
  onSend: (content: string, mentions: MentionRef[]) => void;
  onCancel: () => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onRetry?: () => void;
}

function ChatSidecar({
  collapsed,
  onExpand,
  onCollapse,
  messages,
  activeAdIds,
  isStreaming,
  totalAdCount,
  mentionItems,
  chatError,
  onSend,
  onCancel,
  onSelectAll,
  onClearSelection,
  onRetry,
}: ChatSidecarProps) {
  // Surface "chat unavailable" with retry if the session fetch failed.
  const errorBanner = chatError
    ? 'Studio chat unavailable — the API service may not be running.'
    : null;

  return (
    <aside
      className="flex flex-col border-l border-slate-200 bg-white h-full overflow-hidden transition-[width] duration-200 ease-out flex-shrink-0"
      style={{ width: collapsed ? CHAT_COLLAPSED_WIDTH : CHAT_WIDTH }}
    >
      {collapsed ? (
        <div className="flex flex-col items-center pt-3 pb-4 gap-1">
          <button
            onClick={onExpand}
            title="Open chat"
            className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center text-brand-teal-dark hover:bg-teal-100 transition mb-1"
          >
            <ChevronLeft size={15} />
          </button>
          <div className="w-5 border-t border-slate-100 my-1" />
          <button
            onClick={onExpand}
            title="Open chat"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-50 transition"
          >
            <MessageSquare size={14} />
          </button>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          <header className="px-3 py-2 border-b border-slate-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare size={12} className="text-brand-teal-dark" />
                <span className="text-xs font-bold text-slate-700">Studio Chat</span>
              </div>
              <button
                onClick={onCollapse}
                title="Collapse chat"
                className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
              >
                <ChevronRight size={14} />
              </button>
            </div>
            {chatError && onRetry && (
              <div className="mt-2 flex items-center justify-between gap-2 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5">
                <span className="text-[10px] text-rose-600 leading-snug">
                  Chat unavailable — API may not be running.
                </span>
                <button
                  onClick={onRetry}
                  className="shrink-0 flex items-center gap-1 text-[10px] font-semibold text-rose-600 hover:text-rose-800 transition"
                  title="Retry connection"
                >
                  <RefreshCw size={10} />
                  Retry
                </button>
              </div>
            )}
          </header>
          <div className="flex-1 min-h-0">
            <ChatPanel
              messages={messages}
              activeAdIds={activeAdIds}
              isStreaming={isStreaming}
              editsRemaining={null}
              totalAdCount={totalAdCount}
              onSend={onSend}
              onCancel={onCancel}
              onSelectAll={onSelectAll}
              onClearSelection={onClearSelection}
              mentionItems={mentionItems}
              errorBanner={null}
            />
          </div>
        </div>
      )}
    </aside>
  );
}
